import React, { useState, useRef, useEffect } from "react";
import { Icon, Input, Button, Image, Grid, Modal, Header } from "semantic-ui-react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { useMsal } from "@azure/msal-react";
import { logoutUser } from "../actions/authActions";
import AgentRequestStatus from "./AgentRequestStatus";
import "../styles/AiNavbar.css";

const AiNavbar = ({
  setToggle,
  navTitle,
  type,
  handleFilterSort,
  handleChatHistoryOpen,
  ChatHistoryOpen,
  setChatHistoryOpen,
  activeTab,
  setActiveTab,
  datasetId: propDatasetId,
  isAssistantDataset, // New prop
  uploadedFiles = [], // New prop for uploaded files
  documentCount = 0, // New prop for document count from API
  isFullscreen = false,
  onToggleFullscreen,
  chatViewPreference = "same-side",
  onChatViewChange,
  showQuitAgent = false,
  onQuitAgent,
  agentRequestStatus = null,
}) => {
  const [showChatViewDropdown, setShowChatViewDropdown] = useState(false);
  const chatViewDropdownRef = useRef(null);
  const [isLogoutModalOpen, setLogoutModalOpen] = useState(false);
  const dispatch = useDispatch();
  const { instance } = useMsal();

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (chatViewDropdownRef.current && !chatViewDropdownRef.current.contains(e.target)) {
        setShowChatViewDropdown(false);
      }
    };
    if (showChatViewDropdown) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showChatViewDropdown]);

  const handleChatViewSelect = (preference) => {
    if (onChatViewChange) onChatViewChange(preference);
    setShowChatViewDropdown(false);
  };
  const navBarTitle = navTitle || "Q&A";
  const navigate = useNavigate();
  const location = useLocation();
  const hasQueryParams = location.search.length > 0;
  const { datasetId: paramDatasetId } = useParams();
  const currentUserType = useSelector((state) => state.auth.user);
  const orgDetails = useSelector((state) => state.admin?.details);

  // Function to get query parameters
  const getQueryParams = (query) => {
    if (!query) return {};
    const q = query.startsWith('?') ? query.slice(1) : query;
    const sp = new URLSearchParams(q);
    const obj = {};
    for (const [k, v] of sp.entries()) obj[k] = v;
    return obj;
  };

  const queryParams = getQueryParams(location.search.substring(1));
  const datasetId = propDatasetId || paramDatasetId || queryParams.c;
  const currentUrl = window.location.href;
  const baseUrl = window.location.origin;
  const relativeUrl = currentUrl.replace(baseUrl, '');
  
  // Check if user is coming from quick chat
  const urlParams = new URLSearchParams(location.search);
  const quickChatParam = urlParams.get("quickChat");
  const methodParam = urlParams.get("method");
  const isQuickChatMode = quickChatParam === "1" && (methodParam === "quickChat" || methodParam === "assistantQuickChat");

  const handleLogout = () => {
    setLogoutModalOpen(true);
  };

  const confirmLogout = () => {
    dispatch(logoutUser());
    instance.logoutRedirect();
    setLogoutModalOpen(false);
  };

  const cancelLogout = () => {
    setLogoutModalOpen(false);
  };

  const handleBack = () => {
    setChatHistoryOpen(false); // Set the state to false
    console.log("relativeUrl", relativeUrl);
    if (relativeUrl) {
      navigate(relativeUrl);
    } else {
      navigate(`/aiassistant`);
    }
  };

  const handleView = () => {
    setChatHistoryOpen(false); // Set the state to false
    navigate(`/aiassistant`);
  };

  /*useState(() => {
    if(window.innerWidth < 320 && ChatHistoryOpen) {
      setChatHistoryOpen(false);
    });*/

  return (
    <>
    <Grid
      className="aiAssiatantRow"
      columns={2}
      divided={false}
      style={{ marginLeft: (currentUserType === "ExternalUser" || currentUserType === "HumanAgent") ? "0" : "228px" }}
      stackable
    >
      <Grid.Row className="aiAssiatantHeaderRow">
        <div className="aiAssistantSegment">
          {/* Mobile view */}
          {currentUserType !== "ExternalUser" && currentUserType !== "HumanAgent" && (
            <Grid.Column className="mobile-sidebar-icon">
              <Grid.Column>
                <div
                  onClick={setToggle}
                  className="button-mobile-sidebar-image mobileMenu"
                >
                  <Image src="/images/mobile_menu.svg" alt="Menu Icon" />
                </div>
              </Grid.Column>
            </Grid.Column>
          )}
          {/* Mobile view end */}

          <Grid.Column className="aiAssiatantFirstDiv">
            <Grid.Column>
              {currentUserType === "ExternalUser" ? (
                <div className="menu-item-left external-header">
                  <div className="external-brand-card">
                    <div className="external-badge">IA</div>
                    <div className="external-titles">
                      <label className="external-title">{orgDetails?.orgName ? `${orgDetails.orgName} AI` : "Innover AI"}</label>
                      <div className="external-subtitle">Dedicated chat for external users</div>
                    </div>
                  </div>
                </div>
              ) : currentUserType === "HumanAgent" ? (
                <div className="menu-item-left">
                  <Image src="/images/support-agent.svg" alt="Support Agent" />
                  <label>Support Agent</label>
                </div>
              ) : (
                <div className="menu-item-left">
                  <Image
                    src={isQuickChatMode
                      ? "/images/quick-chat-icon.svg"
                      : "/images/ai-assistant.svg"}
                    alt="ai assitant"
                  />
                  <label>{navBarTitle || "Default"}</label>
                </div>
              )}
            </Grid.Column>
          </Grid.Column>
          {/* Mobile view */}
          {currentUserType !== "ExternalUser" && (
            <Grid.Column className="mobile-history-icon">
              <Grid.Column>
                <div
                  className="button-mobile-history-image chat-history-icon-mobile"
                  onClick={handleChatHistoryOpen}
                >
                  <Image
                    src="/images/chat-history.svg"
                    alt="Chat History"
                    data-cy="chat-history-img"
                  />
                </div>
              </Grid.Column>
            </Grid.Column>
          )}
          {/* Mobile view end */}
          <Grid.Column className="aiAssiatantSecondDiv">
            <Grid.Column className="ai-navbar-actions">
              {showQuitAgent && (
                <button
                  className="button-quit-agent"
                  onClick={onQuitAgent}
                  aria-label="Stop Live Agent"
                  data-cy="quit-agent-btn-cy"
                >
                  ✕ Stop Live Agent
                </button>
              )}
              {currentUserType !== "ExternalUser" && currentUserType !== "HumanAgent" && (
                <AgentRequestStatus status={agentRequestStatus} />
              )}
              {currentUserType !== "ExternalUser" && currentUserType !== "QuickChatUser" && hasQueryParams && (
                <button
                  className="button-ai-fullscreen"
                  onClick={onToggleFullscreen}
                  aria-label={isFullscreen ? "Exit Full Screen" : "Full Screen"}
                >
                  {isFullscreen ? "Exit Full Screen" : "Full Screen"}
                </button>
              )}
              {currentUserType === "HumanAgent" && (
                <div
                  className="profile-logout"
                  onClick={handleLogout}
                  style={{ display: "flex", alignItems: "center", gap: "6px", cursor: "pointer" }}
                >
                  <img src="/images/log_out.svg" alt="Logout" style={{ width: "18px", height: "18px" }} />
                  <span style={{ fontSize: "14px" }}>Logout</span>
                </div>
              )}
              {currentUserType !== "ExternalUser" && currentUserType !== "QuickChatUser" && currentUserType !== "HumanAgent" && (
                <Button
                  primary
                  className="button-ai-new-chat"
                  onClick={() => handleView()}
                  disabled={!hasQueryParams}
                  data-cy="newchat-btn-click-cy"
                >
                  <div className="button-ai-console-parent">
                    <Image
                      src="/images/white-chat-icons.svg"
                      alt="Add new Dataset"
                      data-cy="newchat-img-click-cy"
                    />
                    <label>New Chat</label>
                  </div>
                </Button>
              )}
            </Grid.Column>
          </Grid.Column>
        </div>
        {currentUserType !== "ExternalUser" && (
          <Grid.Column
            className={
              ChatHistoryOpen
                ? "aiAssiatantHistoryHeaderMobile"
                : "aiAssiatantThirdDiv"
            }
          >
            <Grid.Column>
              <div
                className={`${
                  ChatHistoryOpen
                    ? "chatHistoryBoxMobile"
                    : "chatHistoryBox chatHistoryBoxDesktop"
                }`}
              >
                <div
                  className={
                    ChatHistoryOpen
                      ? "chatHistorySubBoxMobile"
                      : "chatHistorySubBoxDesktop"
                  }
                >
                  <div className="backButtonChatHistoryMobile">
                    <Image
                      className="BackButtonMobile"
                      onClick={() => handleBack()}
                      src="/images/Back.svg"
                      alt="Back button"
                    />
                  </div>
                  <div className="tab-container">
                    <div
                      className={`tab ${currentUserType === "User" || currentUserType === "ExternalUser" || currentUserType === "HumanAgent" ? "tabuseractive" : ""} ${
                        activeTab === "chatHistory" ? "active" : ""
                      }`}
                      onClick={() => setActiveTab("chatHistory")}
                    >
                      <label className="tab-label">History</label>
                    </div>
                    {isQuickChatMode && currentUserType !== "HumanAgent" && (
                      <div
                        className={`tab ${
                          activeTab === "files" ? "active" : ""
                        }`}
                        onClick={() => setActiveTab("files")}
                      >
                        <label className="tab-label">Documents</label>
                        {documentCount > 0 && (
                          <span className="tab-badge">{documentCount}</span>
                        )}
                      </div>
                    )}
                    {currentUserType != "User" && currentUserType != "ExternalUser" && currentUserType != "QuickChatUser" && currentUserType != "HumanAgent" && (
                      <div
                        className={`retrieval-tab tab ${
                          activeTab ===
                          (isAssistantDataset ? "assistantSettings" : "retrievalParameter")
                            ? "active"
                            : ""
                        } ${!datasetId ? "disabled" : ""}`}
                        onClick={() => {
                          if (datasetId) {
                            setActiveTab(
                              isAssistantDataset ? "assistantSettings" : "retrievalParameter"
                            );
                          }
                        }}
                      >
                        <label className="tab-label">Playground</label>
                      </div>
                    )}
                    {activeTab === "chatHistory" && currentUserType !== "HumanAgent" && (
                      <div className="filterButton">
                        <img
                          src="/images/filter.svg"
                          alt="View More"
                          style={{ cursor: "pointer" }}
                          onClick={handleFilterSort}
                          data-cy="search-filter-popup-open-click-cy"
                        />
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </Grid.Column>
          </Grid.Column>
        )}
      </Grid.Row>
    </Grid>

    {/* Logout Confirmation Modal for HumanAgent */}
    {currentUserType === "HumanAgent" && (
      <Modal
        open={isLogoutModalOpen}
        onClose={cancelLogout}
        size="tiny"
        className="logout-modal"
        mountNode={document.body}
      >
        <Modal.Content>
          <Header as="h3">Logout</Header>
          <p style={{ textAlign: "center" }}>
            Are you sure you want to log out?
          </p>
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={cancelLogout}>Cancel</Button>
          <Button onClick={confirmLogout} primary>
            Logout
          </Button>
        </Modal.Actions>
      </Modal>
    )}
  </>
  );
};

export default AiNavbar;
